import React, { PropsWithChildren, useRef, useState } from 'react';
import Image from 'next/image';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { Entry } from 'contentful';
import {
  TypeKeyValueListFields,
  TypeLink_externalFields,
  TypeLink_internalFields,
} from '@/core/generated-types';
import { generateTranslationsObject } from '@/utils/translations';
import { UniversalCta } from '@/components';
import { obsessionVideoSideBySideTranslations } from '../types';

export interface ObsessionVideoSideBySideProps {
  cta: Entry<TypeLink_externalFields | TypeLink_internalFields> | undefined;
  t: Entry<Record<string, any>>;
  urlDesktop: string | undefined;
  urlMobile: string | undefined;
}

export const ObsessionVideoSideBySide: React.FC<
  ObsessionVideoSideBySideProps
> = ({ t, cta, urlDesktop, urlMobile }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  useIntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && videoRef.current) {
          if (!hasStartedPlaying) {
            videoRef.current.play();
            setHasStartedPlaying(true);
          }
        } else if (videoRef.current && !hasStartedPlaying) {
          videoRef.current.pause();
        }
      });
    },
    videoRef,
    { threshold: 1 },
  );

  if (!t) return null;

  const { title, item1, item2, item3 } = generateTranslationsObject(
    t.fields as TypeKeyValueListFields,
  ) as obsessionVideoSideBySideTranslations;

  const listIcon =
    'https://images.ctfassets.net/0sea1vycfyqy/6uxFIDIxdK75AZEZ5CjYj4/520ab3acee3d0f3c441f990bd33fece4/CheckMark_Brushstroke.png';

  const ListItem = ({ children }: PropsWithChildren) => (
    <li className="flex items-top">
      <Image
        width={32}
        height={32}
        alt="check-mark"
        className="w-8 h-8 mr-2 lg:mr-4 lg:w-[40px] lg:h-[40px]"
        src={listIcon}
      />
      <p className="font-400 text-[18px] leading-6 lg:text-[20px] lg:leading-8">
        {children}
      </p>
    </li>
  );

  const listItems = [item1, item2, item3];

  return (
    <div className="lg:flex">
      <div className="lg:w-1/2 relative">
        {/*aspect-square 1:1*/}
        <video
          ref={videoRef}
          className="aspect-square p-0 hidden lg:block"
          playsInline
          autoPlay
          loop
          muted
        >
          <source src={urlDesktop} type="video/mp4" />
        </video>
        {/*aspect-video 16:9*/}
        <video
          ref={videoRef}
          className="aspect-video p-0 lg:hidden"
          playsInline
          autoPlay
          loop
          muted
        >
          <source src={urlMobile} type="video/mp4" />
        </video>
      </div>
      <div className="lg:pl-20 mt-4 lg:mt-0 flex flex-col justify-center lg:max-w-[610px] px-6 lg:pr-6 lg:w-1/2">
        <div className="flex mb-8">
          <p
            dangerouslySetInnerHTML={{ __html: title }}
            className="font-[700] text-[32px] lg:text-[42px] md:w-[29rem] lg:space-y-12 leading-[45px] mt-2 lg:mt-0 "
          />
        </div>
        <div className="lg:w-[31rem] w-full lg:pr-8 mb-8">
          <ul className="lg:space-y-10 space-y-6">
            {listItems.map((item) => (
              <ListItem key={item}>{item}</ListItem>
            ))}
          </ul>
        </div>
        <div className="flex flex-row items-center  w-full  mb-8 lg:mb-0 mt-0 lg:max-w-[261px] border-0">
          {cta && <UniversalCta link={cta} defaultButtonStyle="Primary" />}
        </div>
      </div>
    </div>
  );
};
